import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "../../shadcn/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../shadcn/ui/card";
import { Badge } from "../../shadcn/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../shadcn/ui/dialog";
import * as localService from "../../services/localService";
import billingService from "../../services/billingService";
import logger from "../../utils/logger";
import { showApiMessage } from "../../utils/messageHandler";
import { useCurrencies } from "../../hooks/useCurrencies";
import {
  extractCheckoutUrl,
  getSubscriptionQuantity,
  findActiveRecurringSubscription,
} from "../../utils/subscriptionUtils";
import { clearSubscriptions } from "../../redux/actions/subscription";
import UpgradeSubscriptionModal from "../../components/billing/UpgradeSubscriptionModal";

const BillingManagement = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [currentUser, setCurrentUser] = useState(null);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const { currencies } = useCurrencies();

  // Log currencies when they change
  useEffect(() => {
    logger.log("BILLING MANAGEMENT: Currencies updated, count:", currencies.length);
    if (currencies.length > 0) {
      logger.log("Sample currencies:", currencies.slice(0, 3));
    }
  }, [currencies]);

  const loadBillingData = useCallback(async () => {
    try {
      setIsLoading(true);
      const user = localService.getUser();
      setCurrentUser(user);
      const organizationId = user?.organizationId;

      if (!organizationId) {
        logger.error("No organization ID found");
        return;
      }

      // Load all billing data in parallel
      await Promise.all([
        loadActiveSubscription(organizationId),
        loadTransactions(organizationId),
        loadInvoices(organizationId),
        loadAvailablePackages(),
      ]);
    } catch (error) {
      logger.error("Error loading billing data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  const loadActiveSubscription = async organizationId => {
    try {
      const response = await billingService.getOrganizationSubscriptions({
        organizationId,
        limit: 100,
        status: "USABLE",
        orderBy: "subscriptionDate:desc",
      });

      const subscriptions = response?.data?.data || [];
      const latestActiveSubscription = findActiveRecurringSubscription(subscriptions);

      if (latestActiveSubscription) {
        setActiveSubscription(latestActiveSubscription);
        logger.log("Found active recurring subscription:", latestActiveSubscription);
      } else {
        setActiveSubscription(null);
        logger.log("No ACTIVE recurring subscriptions found.");
      }
    } catch (error) {
      logger.error("Error loading active subscription:", error);
      setActiveSubscription(null);
    }
  };

  const loadTransactions = async organizationId => {
    try {
      const response = await billingService.getTransactions({
        organizationId,
        limit: 10,
        orderBy: "createdAt:desc",
      });
      if (response?.data?.data) {
        setTransactions(response.data.data);
      } else {
        setTransactions([]);
      }
    } catch (error) {
      logger.error("Error loading transactions:", error);
      setTransactions([]);
    }
  };

  const loadInvoices = async organizationId => {
    try {
      const response = await billingService.getInvoices({
        organizationId,
        limit: 10,
        orderBy: "createdAt:desc",
      });
      if (response?.data?.data) {
        setInvoices(response.data.data);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      logger.error("Error loading invoices:", error);
      setInvoices([]);
    }
  };

  const loadAvailablePackages = async () => {
    try {
      const response = await billingService.getApplicablePackages({ limit: 10 });
      if (response?.data?.data) {
        setAvailablePackages(response.data.data);
      } else {
        setAvailablePackages([]);
      }
    } catch (error) {
      logger.error("Error loading available packages:", error);
      setAvailablePackages([]);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const currentUser = localService.getUser();
      const organizationId = currentUser?.organizationId;
      const orgTypeCode = currentUser?.organization?.type?.code || "";

      logger.log("JGNGNNFM: Starting subscription cancellation", { organizationId, orgTypeCode });

      if (!organizationId || !activeSubscription?.id) {
        logger.error("Missing organization ID or subscription ID");
        return;
      }

      const response = await billingService.cancelOrganizationSubscription({
        organizationId,
        subscriptionId: activeSubscription.id,
        reason: "User requested cancellation",
      });

      logger.log("JGNGNNFM: Subscription cancelled successfully", response);
      showApiMessage(response || { status: 200 }, "cancel subscription");
      logger.log("Subscription cancelled successfully");

      logger.log("JGNGNNFM: Closing cancel modal");
      setShowCancelModal(false);

      // Reload billing data to reflect the new cancelled status
      await loadBillingData();
    } catch (error) {
      logger.error("JGNGNNFM: Error cancelling subscription:", error);
      showApiMessage(error, "cancel subscription");
      logger.error("Error cancelling subscription:", error);
    }
  };

  const handleUpgradeSubscription = async (packageId, pkg, quantity = null) => {
    try {
      setIsUpgrading(true);
      const currentUser = localService.getUser();
      const organizationId = currentUser?.organizationId;

      if (!organizationId) {
        logger.error("No organization ID found");
        return;
      }

      const subscriptionQuantity = getSubscriptionQuantity(currentUser, pkg);
      // If quantity is provided (from card component), use it; otherwise use calculated quantity
      const finalQuantity = quantity || subscriptionQuantity;

      const userRole = currentUser?.organizationRole?.code || currentUser?.role || "";

      // Check if user has an active subscription
      const isRecurringPackage = pkg?.recurring !== false;

      if (isRecurringPackage && activeSubscription && activeSubscription.id) {
        // User has active subscription - use CHANGE API to modify it
        logger.log("Changing existing subscription:", {
          organizationId,
          subscriptionId: activeSubscription.id,
          packageId,
          quantity: finalQuantity,
          userRole: userRole,
        });

        const response = await billingService.changeOrganizationSubscription({
          organizationId,
          subscriptionId: activeSubscription.id,
          packageId,
          quantity: finalQuantity,
        });

        logger.log("Change subscription response:", response);
        showApiMessage(response, "change subscription");

        // Extract checkout URL using shared utility
        const checkoutUrl = extractCheckoutUrl(response);

        if (checkoutUrl && typeof checkoutUrl === "string") {
          // Redirect to Paystack checkout for payment
          window.location.href = checkoutUrl;
          return;
        }

        // If no checkout URL, show success and refresh
        logger.log("Subscription changed successfully, refreshing data");
        await loadBillingData();
        setShowUpgradeModal(false);
      } else {
        // User has no active subscription - use CREATE API
        logger.log("Creating new subscription:", {
          organizationId,
          packageId,
          quantity: finalQuantity,
          userRole: userRole,
        });

        const response = await billingService.createOrganizationSubscription({
          organizationId,
          packageId,
          quantity: finalQuantity,
        });

        logger.log("Create subscription response:", response);
        showApiMessage(response, "create subscription");

        // Extract checkout URL using shared utility
        const checkoutUrl = extractCheckoutUrl(response);

        if (checkoutUrl && typeof checkoutUrl === "string") {
          // Redirect to Paystack checkout
          window.location.href = checkoutUrl;
          return;
        }

        // If no checkout URL, show success message and refresh data
        logger.log("No checkout URL found, refreshing subscription data");
        await loadBillingData();
        setShowUpgradeModal(false);
      }
    } catch (error) {
      showApiMessage(error, "upgrade subscription");
      logger.error("Error upgrading subscription:", error);
    } finally {
      setIsUpgrading(false);
    }
  };
  const formatCurrency = (amount, currencyId) => {
    const currency = currencies.find(c => c.id === currencyId);
    const code = currency?.code || "KES";
    return `${code} ${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Split packages into recurring vs add-ons and remove currently active package for recurring list
  const recurringPackages = availablePackages.filter(pkg => pkg.recurring !== false);
  const addOnPackages = availablePackages.filter(pkg => pkg.recurring === false);
  const filteredRecurringPackages = recurringPackages.filter(
    pkg => !activeSubscription || pkg.id !== activeSubscription.servicePackage?.id
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => history.goBack()}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-2"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Billing & Subscription</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Current Subscription */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Current Subscription
                  {activeSubscription &&
                    (activeSubscription.status === "CANCELLED" || activeSubscription.status === "CANCELED" ? (
                      <Badge variant="secondary" className="bg-amber-500 text-white">
                        Cancelled
                      </Badge>
                    ) : (
                      <Badge variant="default" className="bg-green-500 text-white">
                        Active
                      </Badge>
                    ))}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activeSubscription ? (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-semibold">
                          {activeSubscription.servicePackage?.name || "Subscription"}
                        </h3>
                        <p className="text-gray-600">
                          {activeSubscription.servicePackage?.description || "No description available"}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold">
                          {formatCurrency(activeSubscription.amount || 0, activeSubscription.currencyId)}
                        </div>
                        <div className="text-gray-500">
                          {activeSubscription.servicePackage?.recurring ? "per month" : "one-time"}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div>
                        <p className="text-sm text-gray-600">Subscription date</p>
                        <p className="font-semibold">
                          {activeSubscription.subscriptionDate
                            ? formatDate(activeSubscription.subscriptionDate)
                            : "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Next renewal</p>
                        <div className="flex items-center">
                          <svg
                            className="w-4 h-4 text-blue-500 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <p className="font-semibold text-blue-600">
                            {activeSubscription.nextRenewalDate
                              ? formatDate(activeSubscription.nextRenewalDate)
                              : "N/A"}
                          </p>
                        </div>
                        {activeSubscription.nextRenewalDate && (
                          <p className="text-xs text-gray-500 mt-1">
                            {Math.ceil(
                              (new Date(activeSubscription.nextRenewalDate) - new Date()) / (1000 * 60 * 60 * 24)
                            )}{" "}
                            days remaining
                          </p>
                        )}
                      </div>
                    </div>

                    {(activeSubscription.status === "CANCELLED" || activeSubscription.status === "CANCELED") &&
                      activeSubscription.nextRenewalDate && (
                        <div className="mb-6">
                          <p className="text-sm text-amber-600 font-medium">
                            Your subscription has been cancelled but will remain active and usable until{" "}
                            {formatDate(activeSubscription.nextRenewalDate)}.
                          </p>
                        </div>
                      )}

                    <div className="flex gap-4">
                      <Button
                        onClick={() => setShowUpgradeModal(true)}
                        className="bg-black text-white hover:bg-gray-800"
                      >
                        Update subscription
                      </Button>
                      {activeSubscription.status !== "CANCELLED" && activeSubscription.status !== "CANCELED" && (
                        <Button
                          onClick={() => setShowCancelModal(true)}
                          variant="outline"
                          className="text-gray-600 border-gray-300"
                        >
                          Cancel subscription
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600 mb-4">No active subscription found</p>
                    <Button onClick={() => history.push("/membership-plan")}>Choose a Plan</Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Invoices */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Invoices</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {invoices.length > 0 ? (
                    invoices.slice(0, 5).map(invoice => (
                      <div key={invoice.id} className="flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-sm">{invoice.referenceCode || "Invoice"}</p>
                          <p className="text-xs text-gray-600">{formatDate(invoice.createdAt)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm">
                            {formatCurrency(invoice.amount || 0, invoice.currencyId)}
                          </p>
                          <Badge
                            variant={invoice.status === "PAID" ? "default" : "outline"}
                            className={invoice.status === "PAID" ? "bg-green-500 text-white" : ""}
                          >
                            {invoice.status || "PENDING"}
                          </Badge>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-600 text-sm">No recent invoices</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Billing Information */}
            <Card>
              <CardHeader>
                <CardTitle>Billing Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-semibold">
                      {currentUser?.firstName} {currentUser?.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-semibold">{currentUser?.emailAddress || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Organization</p>
                    <p className="font-semibold">{currentUser?.organization?.name || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Role</p>
                    <p className="font-semibold">{currentUser?.organizationRole?.name || "N/A"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Transactions */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {transactions.length > 0 ? (
                    transactions.slice(0, 5).map(transaction => {
                      const type = (transaction.type || transaction.transactionType || "").toString().toUpperCase();
                      const isCredit = type === "CREDIT" || transaction.type === true;
                      const label = isCredit ? "Payment" : "Invoice";
                      return (
                        <div key={transaction.id} className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold text-sm">
                              {label}: {transaction.narration || "Transaction"}
                            </p>
                            <p className="text-xs text-gray-600">{formatDate(transaction.createdAt)}</p>
                          </div>
                          <div className="text-right">
                            <p className={`font-semibold text-sm ${isCredit ? "text-green-600" : "text-red-600"}`}>
                              {isCredit ? "+" : "-"}
                              {formatCurrency(transaction.amount || 0, transaction.currencyId)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-600 text-sm">No recent transactions</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-500">
              <span className="mr-2">Powered by</span>
              <span className="font-semibold text-gray-700">theSOFTtribe</span>
            </div>
            <div className="flex space-x-6 text-sm text-gray-500">
              <button onClick={() => history.push("/terms-of-use")} className="hover:text-gray-700">
                Terms
              </button>
              <button onClick={() => history.push("/privacy-policy")} className="hover:text-gray-700">
                Privacy
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription? You'll lose access to all premium features at the end
              of your current billing period.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-4">
            <Button onClick={() => setShowCancelModal(false)} variant="outline" className="flex-1">
              Keep Subscription
            </Button>
            <Button onClick={handleCancelSubscription} className="flex-1 bg-red-600 hover:bg-red-700">
              Cancel Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UpgradeSubscriptionModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        recurringPackages={filteredRecurringPackages}
        addOnPackages={addOnPackages}
        currentUser={currentUser}
        formatCurrency={formatCurrency}
        isUpgrading={isUpgrading}
        onSelectRecurring={(pkg, quantity) => handleUpgradeSubscription(pkg.id, pkg, quantity)}
        onSelectAddOn={(pkg, quantity) => handleUpgradeSubscription(pkg.id, pkg, quantity)}
        accentColor="#111827"
      />
    </div>
  );
};

export default BillingManagement;
